import { FC, useMemo, useState } from "react";
import { formatISO, parseISO } from "date-fns";
import { AccountingMigrationResponse } from "@common/schemas/accounting-integration.schema";
import { Entity } from "@common/types/entity";
import {
    useUpdateMigrationEndDateMutation,
    useUpdateMigrationStartDateMutation,
} from "@api/accountingIntegration.api";
import { MigrationDateRange } from "@components/accountingMigration/historicalData";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { PlainModal, StandardModalHeader } from "@design-system/Modal/Modal";
import { HStack } from "@design-system/HStack/HStack";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { getMigrationEndDate } from "./utils";
import styles from "../AccountingSettings.module.scss";

interface Props {
    show: boolean;
    onHide: () => void;
    workspaceId: string;
    entity: Entity;
    accountingMigration: AccountingMigrationResponse;
}

function toDateOnly(value: string | undefined | null): string | undefined {
    if (!value) {
        return undefined;
    }
    try {
        return formatISO(parseISO(value), { representation: "date" });
    } catch {
        return undefined;
    }
}

function getStartDateDisabledReason(
    accountingMigration: AccountingMigrationResponse,
): string | undefined {
    if (accountingMigration.accountsMappingFinalizedAt != null) {
        return "Start date is locked since opening balances are posted";
    }
    if (accountingMigration.integrationConnectionId != null) {
        return "Can't change start on a connected migration";
    }
    return undefined;
}

function getEndDateLaterBlockedReason(
    accountingMigration: AccountingMigrationResponse,
): string | undefined {
    if (accountingMigration.integrationConnectionId != null) {
        return "The end date can only be moved earlier on a connected migration";
    }
    return undefined;
}

function getSubmitBlockedReason({
    canChangeStart,
    startDate,
    currentStartDate,
    endDate,
    currentEndDate,
    endDateLaterBlockedReason,
}: {
    canChangeStart: boolean;
    startDate: string | undefined;
    currentStartDate: string | undefined;
    endDate: string | undefined;
    currentEndDate: string | undefined;
    endDateLaterBlockedReason: string | undefined;
}): string | undefined {
    if (!endDate) {
        return "Select an end date";
    }
    if (startDate && startDate > endDate) {
        return "End date must be after the start date";
    }
    if (
        currentEndDate &&
        endDate > currentEndDate &&
        endDateLaterBlockedReason
    ) {
        return endDateLaterBlockedReason;
    }
    const didChangeStart = canChangeStart && startDate !== currentStartDate;
    const didChangeEnd = endDate !== currentEndDate;
    if (!didChangeStart && !didChangeEnd) {
        return "Change a date to update";
    }
    return undefined;
}

export const ChangeMigrationDatesModal: FC<Props> = ({
    show,
    onHide,
    workspaceId,
    entity,
    accountingMigration,
}) => {
    const { error: errorToast } = useToaster();
    const bookkeepingStartDate = toDateOnly(entity.bookkeepingStartDate);
    const currentEndDate = useMemo(
        () => getMigrationEndDate(bookkeepingStartDate),
        [bookkeepingStartDate],
    );
    const currentStartDate = toDateOnly(accountingMigration.startDate);
    const canChangeStart =
        getStartDateDisabledReason(accountingMigration) == null;

    const [startDate, setStartDate] = useState(currentStartDate);
    const [endDate, setEndDate] = useState(currentEndDate);
    const updateStartDate = useUpdateMigrationStartDateMutation({
        workspaceId,
        entityId: entity.id,
    });
    const updateEndDate = useUpdateMigrationEndDateMutation({
        workspaceId,
        entityId: entity.id,
    });

    const submitBlockedReason = getSubmitBlockedReason({
        canChangeStart,
        startDate,
        currentStartDate,
        endDate,
        currentEndDate,
        endDateLaterBlockedReason:
            getEndDateLaterBlockedReason(accountingMigration),
    });

    const handleHide = () => {
        setStartDate(currentStartDate);
        setEndDate(currentEndDate);
        onHide();
    };

    return (
        <PlainModal show={show} onHide={handleHide} size="md">
            <StandardModalHeader>Change migration dates</StandardModalHeader>
            <div className={styles.ledgerModalDescription}>
                <MigrationDateRange
                    startDate={startDate}
                    endDate={endDate}
                    isStartDateDisabled={!canChangeStart}
                    startDateDisabledReason={getStartDateDisabledReason(
                        accountingMigration,
                    )}
                    onStartDateChange={setStartDate}
                    onEndDateChange={setEndDate}
                />
            </div>
            <HStack gap={8} justify="end">
                <Button variant="secondary" size="md" onClick={handleHide}>
                    Cancel
                </Button>
                <ButtonWithLoader
                    variant="primary"
                    size="md"
                    disabled={submitBlockedReason != null}
                    tooltip={submitBlockedReason}
                    loading={
                        updateStartDate.isPending || updateEndDate.isPending
                    }
                    onClick={async () => {
                        if (!endDate) {
                            return;
                        }
                        let didUpdateStart = false;
                        try {
                            if (
                                canChangeStart &&
                                startDate !== currentStartDate
                            ) {
                                await updateStartDate.mutateAsync(
                                    startDate ?? null,
                                );
                                didUpdateStart = true;
                            }
                            if (endDate !== currentEndDate) {
                                await updateEndDate.mutateAsync(endDate);
                            }
                            handleHide();
                        } catch {
                            if (didUpdateStart) {
                                errorToast(
                                    "Updated the start date, but couldn't change the end date",
                                );
                            } else {
                                errorToast("Failed to update migration dates");
                            }
                        }
                    }}
                >
                    Update dates
                </ButtonWithLoader>
            </HStack>
        </PlainModal>
    );
};
